import { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowLeft,
  faArrowUp,
  faFloppyDisk,
  faPlus,
  faRotate,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { getUser, postUser } from "../../helper/http";
import styles from "./DeliveryExportColumnsPage.module.css";

type ColumnMode = "field" | "formula";

interface SourceField {
  key: string;
  label: string;
  valueType: "text" | "number" | "date" | "boolean";
}

interface ExportColumn {
  id?: number;
  key: string;
  title: string;
  sourceField: string | null;
  formula: string | null;
  active: boolean;
}

interface ExportConfigResponse {
  fields: SourceField[];
  columns: ExportColumn[];
}

function errorMessage(error: any, fallback: string) {
  return error?.response?.data?.message || error?.message || fallback;
}

function nextColumnKey(columns: ExportColumn[]) {
  const existing = new Set(columns.map((column) => column.key));
  let suffix = columns.length + 1;
  while (existing.has(`custom_${suffix}`)) suffix++;
  return `custom_${suffix}`;
}

export default function DeliveryExportColumnsPage() {
  const navigate = useNavigate();
  const [fields, setFields] = useState<SourceField[]>([]);
  const [columns, setColumns] = useState<ExportColumn[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const fieldMap = useMemo(
    () => Object.fromEntries(fields.map((field) => [field.key, field])),
    [fields],
  );

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const response = (await getUser(
        "api/delivery/admin/export-columns",
      )) as ExportConfigResponse;
      setFields(Array.isArray(response.fields) ? response.fields : []);
      setColumns(Array.isArray(response.columns) ? response.columns : []);
    } catch (requestError: any) {
      setError(errorMessage(requestError, "Failed to load Excel columns"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const updateColumn = (index: number, patch: Partial<ExportColumn>) => {
    setColumns((current) =>
      current.map((column, columnIndex) =>
        columnIndex === index ? { ...column, ...patch } : column,
      ),
    );
    setSuccess("");
  };

  const changeMode = (index: number, mode: ColumnMode) => {
    updateColumn(
      index,
      mode === "field"
        ? {
            sourceField: fields[0]?.key || "publicId",
            formula: null,
          }
        : { sourceField: null, formula: "0" },
    );
  };

  const move = (index: number, offset: -1 | 1) => {
    const target = index + offset;
    if (target < 0 || target >= columns.length) return;
    setColumns((current) => {
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setSuccess("");
  };

  const addColumn = () => {
    setColumns((current) => [
      ...current,
      {
        key: nextColumnKey(current),
        title: "New column",
        sourceField: fields[0]?.key || "publicId",
        formula: null,
        active: true,
      },
    ]);
    setSuccess("");
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const response = (await postUser("api/delivery/admin/export-columns", {
        columns: columns.map((column) => ({
          key: column.key.trim(),
          title: column.title.trim(),
          sourceField: column.sourceField,
          formula: column.formula?.trim() || null,
          active: column.active,
        })),
      })) as ExportConfigResponse;
      setFields(Array.isArray(response.fields) ? response.fields : fields);
      setColumns(Array.isArray(response.columns) ? response.columns : columns);
      setSuccess("Excel column configuration saved.");
    } catch (requestError: any) {
      setError(errorMessage(requestError, "Failed to save Excel columns"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className={styles.container}>
      <header className={styles.header}>
        <div className={styles.titleRow}>
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => navigate("/delivery-operations")}
            title="Back to Delivery operations"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div>
            <h1>Delivery Excel columns</h1>
            <span>
              Configure the order, names, fields and calculated values.
            </span>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.secondary}
            onClick={load}
            disabled={loading || saving}
          >
            <FontAwesomeIcon icon={faRotate} />
            <span>{loading ? "Loading" : "Reload"}</span>
          </button>
          <button
            type="button"
            onClick={save}
            disabled={loading || saving || !columns.length}
          >
            <FontAwesomeIcon icon={faFloppyDisk} />
            <span>{saving ? "Saving" : "Save changes"}</span>
          </button>
        </div>
      </header>

      <section className={styles.guide} aria-label="Formula help">
        <strong>Formula syntax</strong>
        <span>
          Reference another column by its stable key, for example{" "}
          <code>[weight] / 1000</code>. Supported operations: +, -, *, / and
          parentheses.
        </span>
      </section>

      {error && <div className={styles.error}>{error}</div>}
      {success && <div className={styles.success}>{success}</div>}

      <div className={styles.tableWrap} aria-busy={loading}>
        <table>
          <thead>
            <tr>
              <th>Order</th>
              <th>Enabled</th>
              <th>Column key</th>
              <th>Excel title</th>
              <th>Type</th>
              <th>Delivery field or formula</th>
            </tr>
          </thead>
          <tbody>
            {columns.map((column, index) => {
              const mode: ColumnMode =
                column.formula != null ? "formula" : "field";
              return (
                <tr key={column.key || index}>
                  <td data-label="Order">
                    <div className={styles.orderButtons}>
                      <button
                        type="button"
                        onClick={() => move(index, -1)}
                        disabled={saving || index === 0}
                        title="Move up"
                      >
                        <FontAwesomeIcon icon={faArrowUp} />
                      </button>
                      <button
                        type="button"
                        onClick={() => move(index, 1)}
                        disabled={saving || index === columns.length - 1}
                        title="Move down"
                      >
                        <FontAwesomeIcon icon={faArrowDown} />
                      </button>
                    </div>
                  </td>
                  <td data-label="Enabled">
                    <label className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={column.active}
                        onChange={(event) =>
                          updateColumn(index, { active: event.target.checked })
                        }
                        disabled={saving}
                      />
                      <span>{column.active ? "On" : "Off"}</span>
                    </label>
                  </td>
                  <td data-label="Column key">
                    <input
                      className={styles.keyInput}
                      type="text"
                      value={column.key}
                      onChange={(event) =>
                        updateColumn(index, {
                          key: event.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9_]/g, ""),
                        })
                      }
                      disabled={saving || Boolean(column.id)}
                      aria-label={`Stable key for ${column.title}`}
                    />
                  </td>
                  <td data-label="Excel title">
                    <input
                      type="text"
                      value={column.title}
                      maxLength={120}
                      onChange={(event) =>
                        updateColumn(index, { title: event.target.value })
                      }
                      disabled={saving}
                      aria-label={`Excel title for ${column.key}`}
                    />
                  </td>
                  <td data-label="Type">
                    <select
                      value={mode}
                      onChange={(event) =>
                        changeMode(index, event.target.value as ColumnMode)
                      }
                      disabled={saving}
                    >
                      <option value="field">Delivery field</option>
                      <option value="formula">Formula</option>
                    </select>
                  </td>
                  <td data-label="Delivery field or formula">
                    {mode === "field" ? (
                      <select
                        value={column.sourceField || fields[0]?.key || ""}
                        onChange={(event) =>
                          updateColumn(index, {
                            sourceField: event.target.value,
                          })
                        }
                        disabled={saving}
                      >
                        {fields.map((field) => (
                          <option key={field.key} value={field.key}>
                            {field.label} ({field.valueType})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={column.formula || ""}
                        maxLength={500}
                        placeholder="[weight] / 1000"
                        onChange={(event) =>
                          updateColumn(index, { formula: event.target.value })
                        }
                        disabled={saving}
                        aria-label={`Formula for ${column.key}`}
                      />
                    )}
                    {mode === "field" && column.sourceField && (
                      <small>
                        {fieldMap[column.sourceField]?.label ||
                          column.sourceField}
                      </small>
                    )}
                  </td>
                </tr>
              );
            })}
            {!columns.length && !loading && (
              <tr>
                <td colSpan={6} className={styles.empty}>
                  No columns configured.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <footer className={styles.footer}>
        <button
          type="button"
          className={styles.secondary}
          onClick={addColumn}
          disabled={loading || saving || columns.length >= 100}
        >
          <FontAwesomeIcon icon={faPlus} />
          <span>Add column</span>
        </button>
        <span>
          Disabled columns are kept and may be used by formulas, but are not
          written to Excel.
        </span>
      </footer>
    </main>
  );
}
